import ArrowLeftIcon from '@strata-monorepo/shared-ui/images/icons/ArrowLeft'
import ArrowRightIcon from '@strata-monorepo/shared-ui/images/icons/ArrowRight'
import { Button } from '@strata-monorepo/shared-ui/components/atoms/Button'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'
import { useStoreState } from '../../store/useStore'
import Spinner from '@strata-monorepo/shared-ui/images/icons/Spinner'
import useElementClicked from '@strata-monorepo/tracking/src/hooks/useElementClicked'
import {
  ActionOutcome,
  CustomDimensionsKeys,
  ElementType,
} from '@strata-monorepo/tracking/src/types'

const FOOTER_NAV_BUTTON =
  'strata:w-full strata:flex-1 strata:min-w-0 strata:shrink-0 strata:md:w-[196px] strata:md:flex-none strata:md:max-w-[196px] strata:rounded-sm'

const STICKY_NAV_LG =
  'strata:min-h-[44px] strata:px-5 strata:py-3 strata:text-base strata:font-bold strata:gap-2'

export function QuestionnaireFooter({
  canSkip,
  goNext,
  disabled,
  skipQuestionLabel,
  goBack,
  restrictedBack,
  onSkipLinkClick,
  nextLabel = 'Next',
}: {
  canSkip?: boolean
  goNext: (options?: { skipValidation?: boolean }) => void
  disabled: boolean
  skipQuestionLabel?: string
  goBack: () => void
  restrictedBack?: boolean
  onSkipLinkClick?: () => void
  nextLabel?: string
}) {
  const trackElementClicked = useElementClicked()

  const apiRequestIsLoading = useStoreState((state) => state.form.apiRequestIsLoading)
  const formId = useStoreState((state) => state.form.formId)
  const currentStepIndex = useStoreState((state) => state.form.currentStepIndex)

  const handlePreviousClick = () => {
    trackElementClicked({
      clickType: ActionOutcome.Back,
      itemName: 'Back',
      elementType: ElementType.Button,
      location: 'QUESTIONNAIRE FOOTER',
      itemTitle: 'Previous',
      itemPosition: '',
      customDimensions: [
        { key: CustomDimensionsKeys.FORM_ID, value: formId },
        { key: CustomDimensionsKeys.STEP_ID, value: `step-${currentStepIndex}` },
      ],
    })
    goBack()
  }

  const handleNextClick = () => {
    trackElementClicked({
      clickType: ActionOutcome.Next,
      itemName: nextLabel,
      elementType: ElementType.Button,
      location: '',
      itemTitle: nextLabel,
      itemPosition: '',
      customDimensions: [
        { key: CustomDimensionsKeys.FORM_ID, value: formId },
        { key: CustomDimensionsKeys.STEP_ID, value: `step-${currentStepIndex}` },
      ],
    })
    goNext()
  }

  const handleSkipQuestionClick = () => {
    trackElementClicked({
      clickType: ActionOutcome.Skip,
      itemName: 'Skip Question',
      elementType: ElementType.Button,
      location: '',
      itemTitle: 'Skip Question',
      itemPosition: '',
      customDimensions: [
        { key: CustomDimensionsKeys.FORM_ID, value: formId },
        { key: CustomDimensionsKeys.STEP_ID, value: `step-${currentStepIndex}` },
      ],
    })
    goNext({ skipValidation: true })
  }

  const handleFooterSkipLinkClick = () => {
    if (onSkipLinkClick) {
      trackElementClicked({
        clickType: ActionOutcome.Skip,
        itemName: 'take_me_to_plans',
        elementType: ElementType.Button,
        location: 'QUESTIONNAIRE FOOTER',
        itemTitle: 'Take Me to Plans',
        itemPosition: '',
        customDimensions: [
          { key: CustomDimensionsKeys.FORM_ID, value: formId },
          { key: CustomDimensionsKeys.STEP_ID, value: `step-${currentStepIndex}` },
        ],
      })
      onSkipLinkClick()
      return
    }
    handleSkipQuestionClick()
  }

  const nextDisabled = disabled || apiRequestIsLoading

  return (
    <div className='strata:sticky strata:bottom-0 strata:z-10 strata:w-full strata:flex strata:flex-col strata:items-center strata:gap-3 strata:py-4 strata:md:gap-2 strata:md:py-3 strata:bg-secondary-100 strata:px-6'>
      <div className='strata:w-full strata:flex strata:flex-row strata:items-center strata:justify-center strata:gap-3 strata:shrink-0 strata:md:w-auto'>
        {!restrictedBack ? (
          <Button
            type='button'
            variant='tertiary'
            onClick={handlePreviousClick}
            className={cn(
              STICKY_NAV_LG,
              'strata:border strata:border-solid strata:border-secondary-500 strata:bg-transparent strata:text-secondary-500 strata:hover:bg-secondary-500 strata:hover:text-white strata:disabled:border-gray-300 strata:disabled:text-gray-300 strata:hover:text-white',
              FOOTER_NAV_BUTTON,
            )}
          >
            <ArrowLeftIcon
              classNames='strata:h-4 strata:w-4 strata:shrink-0'
              aria-hidden
            />
            Previous
          </Button>
        ) : null}
        <Button
          type='button'
          variant='primary'
          onClick={handleNextClick}
          aria-label={nextLabel}
          disabled={nextDisabled}
          className={cn(
            STICKY_NAV_LG,
            'strata:disabled:bg-gray-300 strata:disabled:text-white strata:disabled:opacity-100 strata:disabled:hover:bg-gray-300',
            FOOTER_NAV_BUTTON,
          )}
        >
          {apiRequestIsLoading ? (
            <Spinner classNames='strata:animate-spin' />
          ) : (
            <>
              {nextLabel}
              <ArrowRightIcon
                classNames='strata:h-4 strata:w-4 strata:shrink-0'
                aria-hidden
              />
            </>
          )}
        </Button>
      </div>
      {canSkip ? (
        <Button
          type='button'
          variant='tertiary'
          onClick={handleFooterSkipLinkClick}
          className='strata:underline strata:underline-offset-2 strata:text-black strata:px-2 strata:py-0 strata:text-body-sm strata:font-normal strata:hover:text-primary-500 strata:disabled:opacity-60 strata:shrink-0'
        >
          {skipQuestionLabel}
        </Button>
      ) : null}
    </div>
  )
}
